import { HttpParams } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { RootParams } from '@ftr/api-shared'
import { IdentityProviderName } from '@ftr/contracts/type/account'
import { ButtonComponent, FocusableNoopAnchorDirective } from '@ftr/foundation'
import { AppPaths } from '@ftr/routing-paths'
import { WindowRefService } from '@ftr/window'
import { DeepLinkService } from '../../services'

@Component({
  selector: 'ftr-desktop-login-splash',
  templateUrl: 'desktop-login-splash.component.html',
  styleUrls: ['desktop-login-splash.component.css'],
  imports: [FocusableNoopAnchorDirective, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DesktopLoginSplashComponent implements OnInit {
  @Input() outgoingIdentityProvider: IdentityProviderName | undefined

  @Input() authorizationCode: string | undefined
  @Input() state: string | undefined

  // Only set once we know both auth params are present, so the template doesn't have to check them again
  protected externalLoginUrl: string | undefined

  constructor(
    private readonly router: Router,
    private readonly windowRefService: WindowRefService,
    private readonly deepLinkService: DeepLinkService,
  ) {}

  ngOnInit(): void {
    // Don't do anything when showing the splash page for the desktop app outgoing deep link
    if (this.outgoingIdentityProvider && Object.values(IdentityProviderName).includes(this.outgoingIdentityProvider)) {
      return
    }

    // If they refresh/navigate to this page without the params, forward them to the default page
    if (!this.authorizationCode || !this.state) {
      this.navigateToDefaultPage()
      return
    }

    this.externalLoginUrl = this.buildExternalLoginUrl(this.authorizationCode, this.state)

    this.openExternalLoginDeeplink(this.externalLoginUrl)
    this.router.navigate([AppPaths.DesktopLoginSplash], { replaceUrl: true })
  }

  navigateToDefaultPage(): void {
    this.router.navigate([AppPaths.DefaultPage], { replaceUrl: true })
  }

  protected openExternalLoginDeeplink(url: string): void {
    this.deepLinkService.openDeepLink(url)
  }

  private buildExternalLoginUrl(authorizationCode: string, state: string): string {
    const params = new HttpParams({
      fromObject: {
        [RootParams.AuthorizationCode]: authorizationCode,
        [RootParams.State]: state,
      },
    })

    return `${this.windowRefService.getHostName()}/${AppPaths.ExternalLogin}?${params.toString()}`
  }
}
